import React from "react";
import Box, { BoxProps } from "@mui/material/Box";
import { EmtTypography } from "./EmtTypography";
import { styled, SxProps } from "@mui/material/styles";
import { EmtChip } from "./EmtChip";
import Skeleton from "@mui/material/Skeleton";
import { EmtTheme } from "../types";

export type EmtCompactMessageProps = Omit<BoxProps, "title"> & {
  /**
   * If active, set the background to grey
   * @default false
   */
  active?: boolean;
  /**
   * Set the title of the message
   * @default undefined
   */
  title: React.ReactNode;
  /**
   * Message under the title
   * @default undefined
   */
  message?: React.ReactNode;
  /**
   * Time display in right bottom
   * @example 8:00
   * @default undefined
   */
  time?: React.ReactNode;
  /**
   * Number of new message to read
   * @default undefined
   */
  unreadMessages?: number | string;
  /**
   * Number of count message to read
   * @default undefined
   */
  unreadCount?: number | string | null;
  /**
   * If newContact and unreadMessage are false / 0, change the design of the card
   * @default false
   */
  newContact?: boolean;
  /**
   * Message to display if newContact is true
   * @default undefined
   */
  newContactText?: React.ReactNode;
  /**
   * Avatar component
   */
  avatar: React.ReactNode;

  /**
   * Animals associated with the message
   */
  animals?: any[];

  /**
   * Define if loader is active or not
   * @default false
   */
  loader?: boolean;
  nonce?: string;
};

const ellipsisStyle: SxProps = {
  flex: 1,

  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden",
};
const CompactMessage = styled(Box)(({ theme }: { theme?: EmtTheme }) => ({
  padding: "8px",
  borderRadius: "16px",
  display: "flex",
  gap: "12px",
  alignItems: "center",
  cursor: "pointer",
  transition: "linear 0.1s all",
  "&:hover": {
    boxShadow: "0px 0px 5px 1px rgba(83,83,83,0.10)",
  },
  "&.active": {
    backgroundColor: theme?.palette.greyGradient[100],
    cursor: "default",
    "&:hover": {
      boxShadow: "inherit",
    },
  },
}));

const FlexBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const InlinePulse = styled(Box)(({ theme }: { theme?: EmtTheme }) => ({
  display: "flex",
  padding: "8px",
  width: "100%",
  alignItems: "center",
  background: "white",
  borderRadius: "16px",
  gap: "12px",
  border: `1px solid ${theme?.palette.greyGradient[100]}`,
}));

const TimeTypography = styled(EmtTypography)(
  ({ theme }: { theme?: EmtTheme }) => ({
    marginLeft: "5px",
    color: `${theme?.palette.greyGradient[250]}`,
  })
);

export function EmtCompactMessage(props: EmtCompactMessageProps) {
  const {
    avatar,
    title,
    message,
    newContactText,
    time,
    active = false,
    newContact = false,
    className,
    unreadMessages,
    unreadCount,
    loader,
    animals,
    ...rest
  } = props;

  const newContactBox = (
    <FlexBox
      flexDirection="column"
      flex={1}
      sx={{ alignItems: "stretch", minWidth: 0 }}
    >
      <FlexBox>
        <EmtTypography
          variant="body2"
          color="greyGradient.250"
          sx={{ ...ellipsisStyle }}
        >
          <>{newContactText}</>
        </EmtTypography>
      </FlexBox>
      <FlexBox>
        <EmtTypography variant="body1" sx={{ ...ellipsisStyle }}>
          <>{title}</>
        </EmtTypography>
        {!!time && (
          <TimeTypography variant="caption">
            <>{time}</>
          </TimeTypography>
        )}
      </FlexBox>
    </FlexBox>
  );

  const hasUnreadMessages = unreadMessages && unreadMessages !== "0";
  const defaultBox = (
    <FlexBox
      flexDirection="column"
      flex={1}
      sx={{ alignItems: "stretch", minWidth: 0 }}
    >
      <FlexBox>
        <Box
          sx={{ "& *": { lineHeight: "1.1!important" }, flex: 1, minWidth: 0 }}
        >
          <EmtTypography
            variant="body1"
            fontWeight="700"
            sx={{ ...ellipsisStyle }}
          >
            <>{title}</>
          </EmtTypography>
          {animals && animals.length > 0 && (
            <EmtTypography
              variant="body2"
              color="greyGradient.250"
              mb="6px"
              sx={{ ...ellipsisStyle }}
            >
              {" "}
              &{" "}
              {animals.map((animal, index) => (
                <React.Fragment key={index}>
                  {index > 0 && ", "}
                  {animal.displayName}
                </React.Fragment>
              ))}
            </EmtTypography>
          )}
        </Box>

        {hasUnreadMessages ? (
          <EmtChip
            color="primary"
            type="roundedNumber"
            label={unreadCount}
            sx={{ marginLeft: "5px" }}
          />
        ) : null}
      </FlexBox>
      {animals && animals.length > 0 && (
        <Box sx={{ display: "flex", gap: "4px" }}></Box>
      )}
      <FlexBox>
        <EmtTypography
          variant="body2"
          color={hasUnreadMessages ? "greyGradient.400" : "greyGradient.250"}
          fontWeight={hasUnreadMessages ? "bold" : "default"}
          sx={{ ...ellipsisStyle }}
        >
          <>{message}</>
        </EmtTypography>
        {!!time && (
          <TimeTypography variant="caption">
            <>{time}</>
          </TimeTypography>
        )}
      </FlexBox>
    </FlexBox>
  );

  const loaderBox = (
    <InlinePulse>
      <Skeleton
        variant="rectangular"
        width={60}
        height={48}
        sx={{ borderRadius: "18px" }}
      />
      <Box sx={{ width: "100%" }}>
        <FlexBox>
          <Skeleton variant="text" width="80%" />
        </FlexBox>
        <FlexBox>
          <Skeleton variant="text" width="100%" />
        </FlexBox>
      </Box>
    </InlinePulse>
  );

  return loader ? (
    loaderBox
  ) : (
    <CompactMessage
      className={active ? `${className} active` : className}
      {...rest}
    >
      <>{avatar}</>
      {newContact ? newContactBox : defaultBox}
    </CompactMessage>
  );
}
